import { z } from 'zod';

export const CASE_STATUSES = [
  'SUBMITTED',
  'REVIEWING',
  'POSSIBLE_MATCH',
  'RETURN_DETAILS_PROVIDED',
  'READY_FOR_PICKUP',
  'SHIPPED',
  'RETURNED',
  'CLOSED_NOT_FOUND',
] as const;

export const CASE_ACTORS = ['GUEST', 'VENUE', 'ADMIN', 'SYSTEM'] as const;
export const RETURN_METHODS = ['SHIP', 'PICKUP'] as const;

export type CaseStatus = (typeof CASE_STATUSES)[number];
export type CaseActor = (typeof CASE_ACTORS)[number];
export type ReturnMethod = (typeof RETURN_METHODS)[number];

const TimestampSchema = z.iso.datetime({ offset: true });
const Text = (max: number) => z.string().trim().min(1).max(max);
const OptionalText = (max: number) => Text(max).optional();
const HttpUrlSchema = z
  .url()
  .max(500)
  .refine((value) => value.startsWith('https://') || value.startsWith('http://'), {
    message: 'Only HTTP tracking links are allowed',
  });

export const ShippingDetailsSchema = z
  .object({
    recipientName: Text(200),
    address: Text(1_000),
    phone: OptionalText(40),
    instructions: OptionalText(1_000),
    submittedAt: TimestampSchema,
  })
  .strict();

export const ShipmentSchema = z
  .object({
    carrier: Text(200),
    trackingNumber: Text(120),
    trackingUrl: HttpUrlSchema.optional(),
    shippedAt: TimestampSchema,
  })
  .strict();

export const ReturnDetailsSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('PICKUP') }).strict(),
  z
    .object({
      method: z.literal('SHIP'),
      recipientName: Text(200),
      address: Text(1_000),
      phone: OptionalText(40),
      instructions: OptionalText(1_000),
    })
    .strict(),
]);

export const FulfillmentSchema = z.discriminatedUnion('method', [
  z.object({ method: z.literal('PICKUP'), pickupInstructions: Text(1_000) }).strict(),
  z
    .object({
      method: z.literal('SHIP'),
      carrier: Text(200),
      trackingNumber: Text(120),
      trackingUrl: HttpUrlSchema.optional(),
    })
    .strict(),
]);

export const CaseHistoryEntrySchema = z
  .object({
    id: Text(128),
    at: TimestampSchema,
    actor: z.enum(CASE_ACTORS),
    action: Text(120),
    fromStatus: z.enum(CASE_STATUSES).optional(),
    toStatus: z.enum(CASE_STATUSES),
    publicMessage: OptionalText(500),
    privateNote: OptionalText(1_000),
  })
  .strict();

export type CaseHistoryEntry = z.infer<typeof CaseHistoryEntrySchema>;

export const LostItemCaseV1Schema = z
  .object({
    schemaVersion: z.literal('lost-item-case.v1'),
    reportId: z.uuid(),
    venueId: Text(128),
    status: z.enum(CASE_STATUSES),
    version: z.number().int().positive(),
    guestAccessHash: z.string().regex(/^[a-f0-9]{64}$/),
    venueAccessHash: z.string().regex(/^[a-f0-9]{64}$/),
    accessExpiresAt: TimestampSchema,
    returnMethod: z.enum(RETURN_METHODS).optional(),
    shipping: ShippingDetailsSchema.optional(),
    pickupInstructions: OptionalText(1_000),
    shipment: ShipmentSchema.optional(),
    history: z.array(CaseHistoryEntrySchema).min(1).max(100),
    createdAt: TimestampSchema,
    updatedAt: TimestampSchema,
    expiresAt: TimestampSchema,
  })
  .strict()
  .superRefine((caseRecord, context) => {
    if (caseRecord.createdAt >= caseRecord.expiresAt) {
      context.addIssue({ code: 'custom', path: ['expiresAt'], message: 'Invalid case expiry' });
    }
    if (caseRecord.accessExpiresAt > caseRecord.expiresAt) {
      context.addIssue({
        code: 'custom',
        path: ['accessExpiresAt'],
        message: 'Access cannot outlive the case',
      });
    }
    if (caseRecord.returnMethod === 'SHIP' && !caseRecord.shipping) {
      context.addIssue({
        code: 'custom',
        path: ['shipping'],
        message: 'Shipping details required',
      });
    }
    if (caseRecord.returnMethod === 'PICKUP' && caseRecord.shipping) {
      context.addIssue({
        code: 'custom',
        path: ['shipping'],
        message: 'Pickup has no shipping data',
      });
    }
    if (caseRecord.status === 'SHIPPED' && !caseRecord.shipment) {
      context.addIssue({ code: 'custom', path: ['shipment'], message: 'Shipment required' });
    }
    if (caseRecord.status === 'READY_FOR_PICKUP' && !caseRecord.pickupInstructions) {
      context.addIssue({
        code: 'custom',
        path: ['pickupInstructions'],
        message: 'Pickup instructions required',
      });
    }
  });

export type LostItemCaseV1 = z.infer<typeof LostItemCaseV1Schema>;
export type ReturnDetails = z.infer<typeof ReturnDetailsSchema>;
export type Fulfillment = z.infer<typeof FulfillmentSchema>;
